import React, { memo } from 'react';
import { VIDEO_CONFIG, getFrameBorderStyle } from '../../config/videoConfig.js';

/**
 * Props for the VideoFrame component
 */
interface VideoFrameProps {
  /** Whether this frame represents the local participant */
  isLocal?: boolean;

  /** Whether the frame holds no live video, so it needs the placeholder backdrop */
  isEmpty?: boolean;

  /** Frame contents: a video element, or an avatar placeholder plus badges. */
  children?: React.ReactNode;

  /**
   * Drawn outside the clipped content so it can extend past the border: the
   * speaking ring ({@link SpeakingRing}). Passed as an element, so the frame
   * does not re-render at the rate the audio level is sampled.
   */
  indicator?: React.ReactNode;
}

/**
 * VideoFrame — the chrome around one participant tile.
 *
 * It owns size, border, corner radius and the placeholder backdrop, and
 * nothing else: what goes *inside* (live video, or an avatar and a name) is
 * the caller's decision. The size comes from `--video-tile-height` and
 * `--video-tile-width` in `index.css` (#106), which the grid bounds also read. The frame used to render its own "📷 No video" state,
 * which turned every camera-off participant into an identical anonymous black
 * box; {@link ParticipantVideoTile} now fills that space with a name and an
 * avatar instead.
 *
 * The tile opts back into pointer events on its own, because the grid
 * container is `pointer-events-none` so that clicks land on the Phaser map in
 * the gaps between tiles instead of being swallowed by the overlay's box.
 *
 * @example
 * ```tsx
 * <VideoFrame isLocal>
 *   <VideoTrack trackRef={trackRef} />
 * </VideoFrame>
 * ```
 */
const VideoFrameComponent: React.FC<VideoFrameProps> = ({
  isLocal = false,
  isEmpty = false,
  indicator,
  children
}) => {
  const borderStyle = getFrameBorderStyle(isLocal);

  return (
    <div
      data-testid="video-frame"
      className={`
        ${VIDEO_CONFIG.styles.roundedCorners}
        ${VIDEO_CONFIG.styles.shadow}
        relative
        shrink-0
        pointer-events-auto
        ${VIDEO_CONFIG.styles.borderWidth}
        ${borderStyle}
        h-[var(--video-tile-height)]
        w-[var(--video-tile-width)]
        transition-all
      `}
      style={{
        transitionDuration: `${VIDEO_CONFIG.animations.transitionDuration}ms`,
        transitionTimingFunction: VIDEO_CONFIG.animations.transitionEasing
      }}
    >
      <div
        className={`w-full h-full overflow-hidden rounded-[8px] ${isEmpty ? VIDEO_CONFIG.styles.noVideoBackground : ''} relative`}
      >
        {children}
      </div>
      {/* Outside the clipped content so it can draw beyond the border. */}
      {indicator}
    </div>
  );
};

// Export memoized component for performance
export const VideoFrame = memo(VideoFrameComponent);
VideoFrame.displayName = 'VideoFrame';
